import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Save, Undo, Redo, Type, ArrowLeft, Loader2, CheckCircle, Monitor, Smartphone,
  Bold, Italic, AlignLeft, AlignCenter, AlignRight, Minus, Plus, X, Palette,
  ChevronDown, LetterText, Space, Paintbrush
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { 
  isLightColor, 
  getContrastingTextColor, 
  ensureContrast, 
  generatePalette,
  getSectionColors,
  darkenColor,
  lightenColor
} from '../lib/colorContrast';
import MetaPixelTracker from '../components/MetaPixelTracker';

// ==================== HELPER: Detect if background is dark ====================
const isBackgroundDark = (background) => {
  if (!background) return true; // Default to dark
  const darkBackgrounds = ['dark', 'gradient', 'brand', 'image'];
  return darkBackgrounds.includes(background);
};

// ==================== STYLE EDITOR PANEL ====================
const StyleEditorPanel = ({ 
  isOpen, 
  onClose, 
  elementStyles, 
  onStyleChange,
  elementType,
  brandColors
}) => {
  if (!isOpen) return null;

  const defaultStyles = {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: 0,
    color: '#ffffff',
    textAlign: 'center'
  };

  const styles = { ...defaultStyles, ...elementStyles };

  const fontWeights = [
    { value: 300, label: 'Light' },
    { value: 400, label: 'Regular' },
    { value: 500, label: 'Medium' },
    { value: 600, label: 'Semibold' },
    { value: 700, label: 'Bold' },
    { value: 800, label: 'Extra Bold' },
    { value: 900, label: 'Black' }
  ];

  const presetColors = [
    '#ffffff', '#000000', '#f8fafc', '#94a3b8',
    brandColors.primary, brandColors.secondary,
    '#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6'
  ];

  return (
    <div className="fixed right-0 top-16 bottom-0 w-80 bg-gray-900 border-l border-white/10 shadow-2xl z-50 overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-gray-900 border-b border-white/10 p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Paintbrush className="w-5 h-5 text-indigo-400" />
          <span className="font-semibold text-white">Estilos de Texto</span>
        </div>
        <button 
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-4 space-y-6">
        {/* Font Size */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-white/80 flex items-center gap-2">
              <Type className="w-4 h-4" />
              Tamaño de Fuente
            </label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onStyleChange('fontSize', Math.max(8, styles.fontSize - 2))}
                className="p-1 rounded bg-white/10 hover:bg-white/20 text-white"
              >
                <Minus className="w-3 h-3" />
              </button>
              <input
                type="number"
                value={styles.fontSize}
                onChange={(e) => onStyleChange('fontSize', parseInt(e.target.value) || 16)}
                className="w-16 px-2 py-1 rounded bg-white/10 border border-white/20 text-white text-center text-sm"
              />
              <button
                onClick={() => onStyleChange('fontSize', Math.min(120, styles.fontSize + 2))}
                className="p-1 rounded bg-white/10 hover:bg-white/20 text-white"
              >
                <Plus className="w-3 h-3" />
              </button>
            </div>
          </div>
          <Slider
            value={[styles.fontSize]}
            onValueChange={([val]) => onStyleChange('fontSize', val)}
            min={8}
            max={120}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-white/40">
            <span>8px</span>
            <span>120px</span>
          </div>
        </div>

        {/* Font Weight */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-white/80 flex items-center gap-2">
            <Bold className="w-4 h-4" />
            Peso de Fuente
          </label>
          <div className="grid grid-cols-4 gap-2">
            {fontWeights.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => onStyleChange('fontWeight', value)}
                className={`px-2 py-2 rounded text-xs transition-all ${
                  styles.fontWeight === value
                    ? 'bg-indigo-500 text-white'
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
                style={{ fontWeight: value }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Line Height */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-white/80 flex items-center gap-2">
              <Space className="w-4 h-4" />
              Interlineado
            </label>
            <span className="text-sm text-white/60">{styles.lineHeight.toFixed(2)}</span>
          </div>
          <Slider
            value={[styles.lineHeight * 100]}
            onValueChange={([val]) => onStyleChange('lineHeight', val / 100)}
            min={80}
            max={250}
            step={5}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-white/40">
            <span>Compacto</span>
            <span>Espaciado</span>
          </div>
        </div>

        {/* Letter Spacing */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-white/80 flex items-center gap-2">
              <LetterText className="w-4 h-4" />
              Espaciado de Letras
            </label>
            <span className="text-sm text-white/60">{styles.letterSpacing}px</span>
          </div>
          <Slider
            value={[styles.letterSpacing + 10]}
            onValueChange={([val]) => onStyleChange('letterSpacing', val - 10)}
            min={0}
            max={30}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-white/40">
            <span>-10px</span>
            <span>+20px</span>
          </div>
        </div>

        {/* Text Align */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-white/80">Alineación</label>
          <div className="flex gap-2">
            {[
              { value: 'left', icon: AlignLeft },
              { value: 'center', icon: AlignCenter },
              { value: 'right', icon: AlignRight }
            ].map(({ value, icon: Icon }) => (
              <button
                key={value}
                onClick={() => onStyleChange('textAlign', value)}
                className={`flex-1 py-2 rounded flex items-center justify-center transition-all ${
                  styles.textAlign === value
                    ? 'bg-indigo-500 text-white'
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                <Icon className="w-4 h-4" />
              </button>
            ))}
          </div>
        </div>

        {/* Text Color */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-white/80 flex items-center gap-2">
            <Palette className="w-4 h-4" />
            Color de Texto
          </label>
          <div className="grid grid-cols-6 gap-2">
            {presetColors.map((color) => (
              <button
                key={color}
                onClick={() => onStyleChange('color', color)}
                className={`w-10 h-10 rounded-lg border-2 transition-transform hover:scale-110 ${
                  styles.color === color ? 'border-white scale-110' : 'border-transparent'
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <input
              type="color"
              value={styles.color}
              onChange={(e) => onStyleChange('color', e.target.value)}
              className="w-10 h-10 rounded cursor-pointer"
            />
            <input
              type="text"
              value={styles.color}
              onChange={(e) => onStyleChange('color', e.target.value)}
              className="flex-1 px-3 py-2 rounded bg-white/10 border border-white/20 text-white text-sm"
              placeholder="#ffffff"
            />
          </div>
        </div>

        {/* Quick Presets */}
        <div className="space-y-3 pt-4 border-t border-white/10">
          <label className="text-sm font-medium text-white/80">Presets Rápidos</label>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => {
                onStyleChange('fontSize', 48);
                onStyleChange('fontWeight', 700);
                onStyleChange('lineHeight', 1.2);
              }}
              className="px-3 py-2 rounded bg-white/10 text-white/70 hover:bg-white/20 text-sm"
            >
              Headline Grande
            </button>
            <button
              onClick={() => {
                onStyleChange('fontSize', 32);
                onStyleChange('fontWeight', 600);
                onStyleChange('lineHeight', 1.3);
              }}
              className="px-3 py-2 rounded bg-white/10 text-white/70 hover:bg-white/20 text-sm"
            >
              Headline Mediano
            </button>
            <button
              onClick={() => {
                onStyleChange('fontSize', 18);
                onStyleChange('fontWeight', 400);
                onStyleChange('lineHeight', 1.6);
              }}
              className="px-3 py-2 rounded bg-white/10 text-white/70 hover:bg-white/20 text-sm"
            >
              Párrafo
            </button>
            <button
              onClick={() => {
                onStyleChange('fontSize', 14);
                onStyleChange('fontWeight', 500);
                onStyleChange('letterSpacing', 2);
              }}
              className="px-3 py-2 rounded bg-white/10 text-white/70 hover:bg-white/20 text-sm"
            >
              Subtítulo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== STYLED EDITABLE TEXT ====================
const StyledEditableText = ({ 
  value, 
  onChange, 
  onSelectForStyling,
  elementId,
  selectedElementId,
  customStyles = {},
  className = '', 
  as = 'p', 
  placeholder = '',
  baseStyles = {}
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const elementRef = useRef(null);
  const lastValueRef = useRef(value);

  // Update content when value changes externally (not during editing)
  useEffect(() => {
    if (!isEditing && elementRef.current && value !== lastValueRef.current) {
      elementRef.current.textContent = value || placeholder;
      lastValueRef.current = value;
    }
  }, [value, isEditing, placeholder]);

  const handleClick = (e) => {
    e.stopPropagation();
    setIsEditing(true);
    onSelectForStyling(elementId);
  };

  const handleBlur = () => {
    setIsEditing(false);
    const newValue = elementRef.current?.textContent || '';
    if (newValue !== value && newValue !== placeholder) {
      lastValueRef.current = newValue;
      onChange(newValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && as !== 'p') {
      e.preventDefault();
      elementRef.current?.blur();
    }
    if (e.key === 'Escape') {
      elementRef.current.textContent = value || placeholder;
      elementRef.current?.blur();
    }
  };

  const isSelected = selectedElementId === elementId;
  const Tag = as;

  // Merge base styles with custom styles
  // Prevent white text on light backgrounds
  const shouldUseCustomColor = customStyles.color && 
    !(customStyles.color.toLowerCase() === '#ffffff' && baseStyles.color && baseStyles.color !== '#ffffff');
  
  const mergedStyles = {
    ...baseStyles,
    ...(customStyles.fontSize && { fontSize: `${customStyles.fontSize}px` }),
    ...(customStyles.fontWeight && { fontWeight: customStyles.fontWeight }),
    ...(customStyles.lineHeight && { lineHeight: customStyles.lineHeight }),
    ...(customStyles.letterSpacing && { letterSpacing: `${customStyles.letterSpacing}px` }),
    ...(shouldUseCustomColor && { color: customStyles.color }),
    ...(customStyles.textAlign && { textAlign: customStyles.textAlign }),
    // Ensure LTR text direction
    direction: 'ltr',
    unicodeBidi: 'plaintext',
  };

  return (
    <Tag
      ref={elementRef}
      contentEditable={true}
      suppressContentEditableWarning={true}
      onClick={handleClick}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`${className} cursor-text outline-none transition-all duration-200 ${
        isSelected 
          ? 'ring-2 ring-indigo-500 ring-offset-4 ring-offset-transparent' 
          : 'hover:ring-2 hover:ring-white/30 hover:ring-offset-2'
      }`}
      style={mergedStyles}
      data-element-id={elementId}
    >
      {value || placeholder}
    </Tag>
  );
};

// ==================== HERO BLOCK ====================
const HeroBlock = ({ block, brandColors, expertPhoto, onUpdate, onSelectElement, selectedElementId }) => {
  const content = block.content || {};
  const styles = block.styles || {};
  const elementStyles = block.elementStyles || {};
  
  // Generate complete palette from brand colors
  const palette = generatePalette(brandColors);
  const primaryColor = palette.primary;
  const secondaryColor = palette.secondary;
  
  const getBackgroundStyle = () => {
    switch (styles.background) {
      case 'gradient':
        return {
          background: `linear-gradient(135deg, ${palette.primaryDark} 0%, ${primaryColor} 50%, ${secondaryColor} 100%)`
        };
      case 'dark':
        return { 
          background: `linear-gradient(180deg, ${primaryColor} 0%, ${palette.dark} 50%, ${palette.dark} 100%)` 
        };
      case 'image':
        return {
          backgroundImage: content.backgroundImage ? `url(${content.backgroundImage})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        };
      case 'brand':
        return { backgroundColor: primaryColor };
      case 'light':
        return { 
          background: `linear-gradient(180deg, #ffffff 0%, ${palette.primaryBg} 100%)`
        };
      default:
        // Default to brand gradient for impactful hero
        return { 
          background: `linear-gradient(135deg, ${palette.primaryDark} 0%, ${primaryColor} 100%)`
        };
    }
  };
  
  // Determine text colors based on ACTUAL background luminance for WCAG compliance
  // IMPORTANT: Default hero uses brand gradient which is DARK, so needs WHITE text
  // Only use dark text if explicitly set to 'light' background
  const bgIsLight = styles.background === 'light';
  
  // FORCE white text on ALL hero backgrounds except 'light'
  // This prevents ANY possibility of dark text on dark backgrounds
  const headlineColor = bgIsLight ? '#1f2937' : '#ffffff';
  const subheadlineColor = bgIsLight ? 'rgba(31,41,55,0.85)' : 'rgba(255,255,255,0.95)';
  const preheadlineColor = bgIsLight ? 'rgba(31,41,55,0.7)' : 'rgba(255,255,255,0.85)';
  const ctaBgColor = bgIsLight ? palette.primary : '#ffffff';
  const ctaTextColor = bgIsLight ? palette.textOnPrimary : palette.primaryDark;

  const updateContent = (field, value) => {
    onUpdate(block.id, { content: { ...content, [field]: value } });
  };

  const updateElementStyle = (elementId, styleKey, value) => {
    const currentElementStyles = elementStyles[elementId] || {};
    onUpdate(block.id, { 
      elementStyles: { 
        ...elementStyles, 
        [elementId]: { ...currentElementStyles, [styleKey]: value } 
      } 
    });
  };

  return (
    <div 
      className="relative min-h-[650px] flex items-center justify-center px-6 py-24"
      style={getBackgroundStyle()}
    >
      {styles.background === 'image' && (
        <div className="absolute inset-0 bg-black/60" />
      )}
      
      <div className="relative z-10 max-w-4xl mx-auto text-center">
        {content.preheadline && (
          <StyledEditableText
            value={content.preheadline}
            onChange={(val) => updateContent('preheadline', val)}
            onSelectForStyling={onSelectElement}
            elementId={`${block.id}-preheadline`}
            selectedElementId={selectedElementId}
            customStyles={elementStyles[`${block.id}-preheadline`]}
            className="uppercase tracking-[0.2em] mb-6"
            baseStyles={{ 
              fontSize: '13px', 
              fontWeight: 600, 
              color: preheadlineColor 
            }}
            as="p"
            placeholder="PRE-TITULAR..."
          />
        )}
        
        <StyledEditableText
          value={content.headline}
          onChange={(val) => updateContent('headline', val)}
          onSelectForStyling={onSelectElement}
          elementId={`${block.id}-headline`}
          selectedElementId={selectedElementId}
          customStyles={elementStyles[`${block.id}-headline`]}
          className="mb-8 leading-[1.1]"
          baseStyles={{ 
            fontSize: '52px', 
            fontWeight: 800, 
            color: headlineColor,
            letterSpacing: '-0.02em'
          }}
          as="h1"
          placeholder="Tu Headline Principal..."
        />
        
        <StyledEditableText
          value={content.subheadline}
          onChange={(val) => updateContent('subheadline', val)}
          onSelectForStyling={onSelectElement}
          elementId={`${block.id}-subheadline`}
          selectedElementId={selectedElementId}
          customStyles={elementStyles[`${block.id}-subheadline`]}
          className="mb-10 max-w-2xl mx-auto"
          baseStyles={{ 
            fontSize: '20px', 
            fontWeight: 400, 
            color: subheadlineColor, 
            lineHeight: 1.7 
          }}
          as="p"
          placeholder="Tu subtítulo persuasivo..."
        />
        
        <div className="flex flex-col items-center gap-4">
          <button
            className="group relative px-12 py-5 text-lg font-bold rounded-full transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-3xl"
            style={{ 
              backgroundColor: ctaBgColor, 
              color: ctaTextColor,
              boxShadow: `0 10px 40px ${ctaBgColor}50`
            }}
          >
            <StyledEditableText
              value={content.cta}
              onChange={(val) => updateContent('cta', val)}
              onSelectForStyling={onSelectElement}
              elementId={`${block.id}-cta`}
              selectedElementId={selectedElementId}
              customStyles={elementStyles[`${block.id}-cta`]}
              className="inline"
              baseStyles={{ fontSize: '18px', fontWeight: 700, color: ctaTextColor }}
              as="span"
              placeholder="TEXTO DEL BOTÓN"
            />
            <span className="ml-2 group-hover:translate-x-1 transition-transform inline-block">→</span>
          </button>
          
          {content.ctaSubtext && (
            <StyledEditableText
              value={content.ctaSubtext}
              onChange={(val) => updateContent('ctaSubtext', val)}
              onSelectForStyling={onSelectElement}
              elementId={`${block.id}-ctaSubtext`}
              selectedElementId={selectedElementId}
              customStyles={elementStyles[`${block.id}-ctaSubtext`]}
              baseStyles={{ fontSize: '14px', color: bgIsLight ? 'rgba(31,41,55,0.6)' : 'rgba(255,255,255,0.7)' }}
              as="span"
              placeholder="Texto debajo del botón..."
            />
          )}
        </div>
        
        {styles.showExpertPhoto && expertPhoto && (
          <div className="mt-10">
            <img 
              src={expertPhoto} 
              alt="Expert" 
              className="w-32 h-32 md:w-40 md:h-40 rounded-full mx-auto object-cover border-4 border-white/20 shadow-2xl"
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== BENEFITS BLOCK ====================
const BenefitsBlock = ({ block, brandColors, onUpdate, onSelectElement, selectedElementId }) => {
  const content = block.content || {};
  const styles = block.styles || {};
  const elementStyles = block.elementStyles || {};
  const isDark = isBackgroundDark(styles.background);
  
  // Use brand colors for backgrounds with proper contrast
  const palette = generatePalette(brandColors);
  const primaryColor = palette.primary;
  
  // For dark mode, use subtle brand tint
  const bgColor = isDark 
    ? `linear-gradient(180deg, ${primaryColor}12 0%, ${palette.dark} 100%)`
    : palette.light;
  
  // Text colors with guaranteed contrast
  const titleColor = isDark ? '#ffffff' : '#111827';
  const textColor = isDark ? 'rgba(255,255,255,0.85)' : 'rgba(17,24,39,0.8)';
  const cardBg = isDark 
    ? 'rgba(255,255,255,0.05)' 
    : `linear-gradient(135deg, #ffffff 0%, ${palette.primaryBg} 100%)`;

  const updateContent = (field, value) => {
    onUpdate(block.id, { content: { ...content, [field]: value } });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...(content.items || [])];
    newItems[index] = { ...newItems[index], [field]: value };
    updateContent('items', newItems);
  };
  
  // Dynamic grid columns based on number of items
  // MOBILE: Always 1 column, then expand on larger screens
  const itemCount = (content.items || []).length;
  const gridCols = itemCount <= 3 ? 'grid-cols-1 md:grid-cols-3' 
    : itemCount <= 4 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
    : itemCount <= 6 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
    : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';

  return (
    <div 
      className="py-24 px-6"
      style={{ background: bgColor }}
    >
      <div className="max-w-6xl mx-auto">
        <StyledEditableText
          value={content.title}
          onChange={(val) => updateContent('title', val)}
          onSelectForStyling={onSelectElement}
          elementId={`${block.id}-title`}
          selectedElementId={selectedElementId}
          customStyles={elementStyles[`${block.id}-title`]}
          className="text-center mb-20"
          baseStyles={{ fontSize: '40px', fontWeight: 700, color: titleColor, letterSpacing: '-0.02em' }}
          as="h2"
          placeholder="Título de la sección..."
        />
        
        <div className={`grid ${gridCols} gap-8`}>
          {(content.items || []).map((item, idx) => (
            <div 
              key={`${block.id}-benefit-${idx}`}
              className={`p-8 rounded-2xl transition-all duration-300 hover:transform hover:scale-[1.02] ${
                isDark 
                  ? 'border border-white/10 hover:border-white/20' 
                  : 'shadow-lg hover:shadow-xl border border-gray-100'
              }`}
              style={{ background: cardBg }}
            >
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center mb-5"
                style={{ 
                  backgroundColor: primaryColor,
                  color: palette.textOnPrimary
                }}
              >
                <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {item.icon === 'alert' ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  )}
                </svg>
              </div>
              <StyledEditableText
                value={item.title}
                onChange={(val) => updateItem(idx, 'title', val)}
                onSelectForStyling={onSelectElement}
                elementId={`${block.id}-item-${idx}-title`}
                selectedElementId={selectedElementId}
                customStyles={elementStyles[`${block.id}-item-${idx}-title`]}
                className="mb-3"
                baseStyles={{ fontSize: '18px', fontWeight: 700, color: isDark ? '#ffffff' : '#111827' }}
                as="h3"
                placeholder="Título del beneficio..."
              />
              <StyledEditableText
                value={item.description}
                onChange={(val) => updateItem(idx, 'description', val)}
                onSelectForStyling={onSelectElement}
                elementId={`${block.id}-item-${idx}-desc`}
                selectedElementId={selectedElementId}
                customStyles={elementStyles[`${block.id}-item-${idx}-desc`]}
                baseStyles={{ fontSize: '14px', lineHeight: 1.6, color: isDark ? 'rgba(255,255,255,0.7)' : '#4b5563' }}
                placeholder="Descripción del beneficio..."
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==================== CTA BLOCK ====================
const CTABlock = ({ block, brandColors, onUpdate, onSelectElement, selectedElementId }) => {
  const content = block.content || {};
  const elementStyles = block.elementStyles || {};
  const palette = generatePalette(brandColors);

  const updateContent = (field, value) => {
    onUpdate(block.id, { content: { ...content, [field]: value } });
  };

  return (
    <div 
      className="py-28 px-6"
      style={{
        background: `linear-gradient(135deg, ${palette.primaryDark} 0%, ${palette.primary} 50%, ${palette.secondary} 100%)`
      }}
    >
      <div className="max-w-3xl mx-auto text-center">
        <StyledEditableText
          value={content.headline}
          onChange={(val) => updateContent('headline', val)}
          onSelectForStyling={onSelectElement}
          elementId={`${block.id}-headline`}
          selectedElementId={selectedElementId}
          customStyles={elementStyles[`${block.id}-headline`]}
          className="mb-8"
          baseStyles={{ fontSize: '40px', fontWeight: 800, color: '#ffffff', letterSpacing: '-0.02em' }}
          as="h2"
          placeholder="Titular de cierre..."
        />
        
        <StyledEditableText
          value={content.subheadline}
          onChange={(val) => updateContent('subheadline', val)}
          onSelectForStyling={onSelectElement}
          elementId={`${block.id}-subheadline`}
          selectedElementId={selectedElementId}
          customStyles={elementStyles[`${block.id}-subheadline`]}
          className="mb-12 max-w-xl mx-auto"
          baseStyles={{ fontSize: '20px', color: 'rgba(255,255,255,0.9)', lineHeight: 1.7 }}
          as="p"
          placeholder="Subtítulo persuasivo..."
        />
        
        <button
          className="px-14 py-6 text-xl font-bold rounded-full transition-all duration-300 transform hover:scale-105 bg-white"
          style={{ 
            color: palette.primaryDark,
            boxShadow: '0 20px 50px rgba(0,0,0,0.3)'
          }}
        >
          <StyledEditableText
            value={content.cta}
            onChange={(val) => updateContent('cta', val)}
            onSelectForStyling={onSelectElement}
            elementId={`${block.id}-cta`}
            selectedElementId={selectedElementId}
            customStyles={elementStyles[`${block.id}-cta`]}
            className="inline"
            baseStyles={{ fontSize: '20px', fontWeight: 700, color: palette.primaryDark }}
            as="span"
            placeholder="TEXTO DEL BOTÓN"
          />
          <span className="ml-3 group-hover:translate-x-1 transition-transform">→</span>
        </button>
        
        {content.urgency && (
          <StyledEditableText
            value={content.urgency}
            onChange={(val) => updateContent('urgency', val)}
            onSelectForStyling={onSelectElement}
            elementId={`${block.id}-urgency`}
            selectedElementId={selectedElementId}
            customStyles={elementStyles[`${block.id}-urgency`]}
            className="mt-8"
            baseStyles={{ fontSize: '15px', color: 'rgba(255,255,255,0.75)' }}
            as="p"
            placeholder="Mensaje de urgencia..."
          />
        )}
      </div>
    </div>
  );
};

// ==================== TESTIMONIALS BLOCK ====================
const TestimonialsBlock = ({ block, brandColors, onUpdate, onSelectElement, selectedElementId }) => {
  const content = block.content || {};
  const elementStyles = block.elementStyles || {};

  const updateContent = (field, value) => {
    onUpdate(block.id, { content: { ...content, [field]: value } });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...(content.items || [])];
    newItems[index] = { ...newItems[index], [field]: value };
    updateContent('items', newItems);
  };

  return (
    <div className="py-20 px-6 bg-gradient-to-b from-gray-900 to-black">
      <div className="max-w-5xl mx-auto">
        <StyledEditableText
          value={content.title}
          onChange={(val) => updateContent('title', val)}
          onSelectForStyling={onSelectElement}
          elementId={`${block.id}-title`}
          selectedElementId={selectedElementId}
          customStyles={elementStyles[`${block.id}-title`]}
          className="text-center mb-16"
          baseStyles={{ fontSize: '36px', fontWeight: 700, color: '#ffffff' }}
          as="h2"
          placeholder="Título de testimonios..."
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {(content.items || []).map((item, idx) => (
            <div 
              key={`${block.id}-testimonial-${idx}`}
              className="p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm"
            >
              <div className="flex items-start gap-4 mb-6">
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0"
                  style={{ backgroundColor: brandColors.primary }}
                >
                  {(item.name || 'A').charAt(0)}
                </div>
                <div>
                  <StyledEditableText
                    value={item.name}
                    onChange={(val) => updateItem(idx, 'name', val)}
                    onSelectForStyling={onSelectElement}
                    elementId={`${block.id}-item-${idx}-name`}
                    selectedElementId={selectedElementId}
                    customStyles={elementStyles[`${block.id}-item-${idx}-name`]}
                    baseStyles={{ fontSize: '16px', fontWeight: 700, color: '#ffffff' }}
                    as="p"
                    placeholder="Nombre..."
                  />
                  <StyledEditableText
                    value={item.role}
                    onChange={(val) => updateItem(idx, 'role', val)}
                    onSelectForStyling={onSelectElement}
                    elementId={`${block.id}-item-${idx}-role`}
                    selectedElementId={selectedElementId}
                    customStyles={elementStyles[`${block.id}-item-${idx}-role`]}
                    baseStyles={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)' }}
                    as="p"
                    placeholder="Rol/Cargo..."
                  />
                </div>
              </div>
              <StyledEditableText
                value={item.text}
                onChange={(val) => updateItem(idx, 'text', val)}
                onSelectForStyling={onSelectElement}
                elementId={`${block.id}-item-${idx}-text`}
                selectedElementId={selectedElementId}
                customStyles={elementStyles[`${block.id}-item-${idx}-text`]}
                baseStyles={{ fontSize: '16px', fontStyle: 'italic', lineHeight: 1.6, color: 'rgba(255,255,255,0.8)' }}
                placeholder="Texto del testimonio..."
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==================== FEATURES BLOCK ====================
const FeaturesBlock = ({ block, brandColors, onUpdate, onSelectElement, selectedElementId }) => {
  const content = block.content || {};
  const styles = block.styles || {};
  const elementStyles = block.elementStyles || {};
  const isDark = isBackgroundDark(styles.background);

  const updateContent = (field, value) => {
    onUpdate(block.id, { content: { ...content, [field]: value } });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...(content.items || [])];
    newItems[index] = { ...newItems[index], [field]: value };
    updateContent('items', newItems);
  };

  return (
    <div 
      className="py-20 px-6"
      style={{ 
        backgroundColor: isDark ? '#0f0f1a' : '#f8fafc',
        background: styles.background === 'gradient' 
          ? `linear-gradient(135deg, ${brandColors.primary}10 0%, ${brandColors.secondary || '#8B5CF6'}10 100%)`
          : undefined
      }}
    >
      <div className="max-w-5xl mx-auto">
        <StyledEditableText
          value={content.title}
          onChange={(val) => updateContent('title', val)}
          onSelectForStyling={onSelectElement}
          elementId={`${block.id}-title`}
          selectedElementId={selectedElementId}
          customStyles={elementStyles[`${block.id}-title`]}
          className="text-center mb-16"
          baseStyles={{ fontSize: '36px', fontWeight: 700, color: isDark ? '#ffffff' : '#111827' }}
          as="h2"
          placeholder="Título de características..."
        />
        
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {(content.items || []).map((item, idx) => (
            <div 
              key={`${block.id}-feature-${idx}`}
              className={`p-6 rounded-2xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200 shadow-lg'}`}
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: `${brandColors.primary}20` }}
              >
                <svg className="w-6 h-6" style={{ color: brandColors.primary }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <StyledEditableText
                value={item.title}
                onChange={(val) => updateItem(idx, 'title', val)}
                onSelectForStyling={onSelectElement}
                elementId={`${block.id}-item-${idx}-title`}
                selectedElementId={selectedElementId}
                customStyles={elementStyles[`${block.id}-item-${idx}-title`]}
                className="mb-2"
                baseStyles={{ fontSize: '18px', fontWeight: 600, color: isDark ? '#ffffff' : '#111827' }}
                as="h3"
                placeholder="Título característica..."
              />
              <StyledEditableText
                value={item.value || item.description}
                onChange={(val) => updateItem(idx, 'value', val)}
                onSelectForStyling={onSelectElement}
                elementId={`${block.id}-item-${idx}-value`}
                selectedElementId={selectedElementId}
                customStyles={elementStyles[`${block.id}-item-${idx}-value`]}
                baseStyles={{ fontSize: '14px', color: isDark ? 'rgba(255,255,255,0.7)' : '#6b7280' }}
                placeholder="Valor o descripción..."
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==================== VIDEO BLOCK ====================
const VideoBlock = ({ block, brandColors, onUpdate, onSelectElement, selectedElementId }) => {
  const content = block.content || {};
  const styles = block.styles || {};
  const elementStyles = block.elementStyles || {};
  const isDark = isBackgroundDark(styles.background);

  const updateContent = (field, value) => {
    onUpdate(block.id, { content: { ...content, [field]: value } });
  };

  return (
    <div 
      className="py-20 px-6"
      style={{ backgroundColor: isDark ? '#0f0f1a' : '#f8fafc' }}
    >
      <div className="max-w-4xl mx-auto">
        <StyledEditableText
          value={content.title}
          onChange={(val) => updateContent('title', val)}
          onSelectForStyling={onSelectElement}
          elementId={`${block.id}-title`}
          selectedElementId={selectedElementId}
          customStyles={elementStyles[`${block.id}-title`]}
          className="text-center mb-10"
          baseStyles={{ fontSize: '36px', fontWeight: 700, color: isDark ? '#ffffff' : '#111827' }}
          as="h2"
          placeholder="Título del video..."
        />
        
        <div className="relative aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
          {content.videoUrl ? (
            <video src={content.videoUrl} controls className="w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
              <div 
                className="w-24 h-24 rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-transform"
                style={{ backgroundColor: brandColors.primary }}
              >
                <svg className="w-12 h-12 text-white ml-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
              <p className="text-white/60 text-sm mt-4">Haz clic para añadir un video</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== FAQ BLOCK ====================
const FAQBlock = ({ block, brandColors, onUpdate, onSelectElement, selectedElementId }) => {
  const content = block.content || { title: 'Preguntas Frecuentes', items: [] };
  const styles = block.styles || {};
  const elementStyles = block.elementStyles || {};
  const isDark = isBackgroundDark(styles.background);
  const [openIndex, setOpenIndex] = useState(-1);

  const updateContent = (field, value) => {
    onUpdate(block.id, { content: { ...content, [field]: value } });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...(content.items || [])];
    newItems[index] = { ...newItems[index], [field]: value };
    updateContent('items', newItems);
  };

  return (
    <div 
      className="py-20 px-6"
      style={{ backgroundColor: isDark ? '#0f0f1a' : '#f8fafc' }}
    >
      <div className="max-w-3xl mx-auto">
        <StyledEditableText
          value={content.title}
          onChange={(val) => updateContent('title', val)}
          onSelectForStyling={onSelectElement}
          elementId={`${block.id}-title`}
          selectedElementId={selectedElementId}
          customStyles={elementStyles[`${block.id}-title`]}
          className="text-center mb-12"
          baseStyles={{ fontSize: '36px', fontWeight: 700, color: isDark ? '#ffffff' : '#111827' }}
          as="h2"
          placeholder="Preguntas Frecuentes..."
        />
        
        <div className="space-y-4">
          {(content.items || []).map((item, idx) => (
            <div 
              key={`${block.id}-faq-${idx}`}
              className="rounded-2xl overflow-hidden border"
              style={{ 
                backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#ffffff',
                borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
              }}
            >
              <button
                onClick={() => setOpenIndex(openIndex === idx ? -1 : idx)}
                className="w-full px-6 py-5 flex items-center justify-between text-left transition-all"
                style={{ backgroundColor: openIndex === idx ? `${brandColors.primary}10` : 'transparent' }}
              >
                <StyledEditableText
                  value={item.question}
                  onChange={(val) => { updateItem(idx, 'question', val); }}
                  onSelectForStyling={onSelectElement}
                  elementId={`${block.id}-faq-${idx}-q`}
                  selectedElementId={selectedElementId}
                  customStyles={elementStyles[`${block.id}-faq-${idx}-q`]}
                  baseStyles={{ fontSize: '18px', fontWeight: 600, color: isDark ? '#ffffff' : '#1f2937' }}
                  as="span"
                  placeholder="Pregunta..."
                  onClick={(e) => e.stopPropagation()}
                />
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ml-4"
                  style={{ backgroundColor: openIndex === idx ? brandColors.primary : `${brandColors.primary}20` }}
                >
                  <ChevronDown 
                    className={`w-5 h-5 transition-transform duration-300 ${openIndex === idx ? 'rotate-180' : ''}`}
                    style={{ color: openIndex === idx ? '#ffffff' : brandColors.primary }}
                  />
                </div>
              </button>
              
              <div 
                className={`overflow-hidden transition-all duration-300 ${openIndex === idx ? 'max-h-96' : 'max-h-0'}`}
              >
                <div className="px-6 pb-5">
                  <StyledEditableText
                    value={item.answer}
                    onChange={(val) => updateItem(idx, 'answer', val)}
                    onSelectForStyling={onSelectElement}
                    elementId={`${block.id}-faq-${idx}-a`}
                    selectedElementId={selectedElementId}
                    customStyles={elementStyles[`${block.id}-faq-${idx}-a`]}
                    baseStyles={{ fontSize: '15px', lineHeight: 1.7, color: isDark ? 'rgba(255,255,255,0.7)' : '#4b5563' }}
                    placeholder="Respuesta..."
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==================== BLOCK RENDERER ====================
const BlockRenderer = ({ block, brandColors, expertPhoto, onUpdate, onSelectElement, selectedElementId }) => {
  const props = { block, brandColors, expertPhoto, onUpdate, onSelectElement, selectedElementId };
  
  switch (block.type) {
    case 'hero':
      return <HeroBlock {...props} />;
    case 'benefits':
      return <BenefitsBlock {...props} />;
    case 'features':
      return <FeaturesBlock {...props} />;
    case 'testimonials':
      return <TestimonialsBlock {...props} />;
    case 'cta':
      return <CTABlock {...props} />;
    case 'video':
      return <VideoBlock {...props} />;
    case 'faq':
      return <FAQBlock {...props} />;
    default:
      return (
        <div className="py-16 px-6 bg-gray-100 text-center">
          <p className="text-gray-500">Bloque no reconocido: {block.type}</p>
        </div>
      );
  }
};

// ==================== MAIN PREVIEW COMPONENT ====================
const LandingPreview = () => {
  const { landingId } = useParams();
  const [blocks, setBlocks] = useState([]);
  const [briefData, setBriefData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [viewMode, setViewMode] = useState('desktop');
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [selectedElementId, setSelectedElementId] = useState(null);

  const brandColors = {
    primary: briefData?.primaryColor || briefData?.brandAssets?.primaryColor || '#6366f1',
    secondary: briefData?.secondaryColor || briefData?.brandAssets?.secondaryColor || '#00D68F',
    accent: briefData?.accentColor || briefData?.brandAssets?.accentColor || '#f59e0b'
  };

  const expertPhoto = briefData?.brandAssets?.expertPhoto || briefData?.expertPhoto;

  // Load data from localStorage
  useEffect(() => {
    const storedData = localStorage.getItem(`preview_${landingId}`);
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        setBlocks(parsed.blocks || []);
        setBriefData(parsed.briefData || {});
        setHistory([parsed.blocks || []]);
        setHistoryIndex(0);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error('Error loading preview data:', e);
      }
    }
  }, [landingId]);

  // Get current element styles
  const getCurrentElementStyles = () => {
    if (!selectedElementId) return {};
    
    for (const block of blocks) {
      if (block.elementStyles && block.elementStyles[selectedElementId]) {
        return block.elementStyles[selectedElementId];
      }
    }
    return {};
  };

  // Handle style change
  const handleStyleChange = (styleKey, value) => {
    if (!selectedElementId) return;
    
    setBlocks(prevBlocks => {
      const newBlocks = prevBlocks.map(block => {
        // Check if this element belongs to this block
        if (selectedElementId.startsWith(block.id)) {
          const currentElementStyles = block.elementStyles || {};
          const currentStyles = currentElementStyles[selectedElementId] || {};
          return {
            ...block,
            elementStyles: {
              ...currentElementStyles,
              [selectedElementId]: {
                ...currentStyles,
                [styleKey]: value
              }
            }
          };
        }
        return block;
      });
      
      // Add to history
      setHistory(prev => {
        const newHistory = prev.slice(0, historyIndex + 1);
        newHistory.push(newBlocks);
        return newHistory;
      });
      setHistoryIndex(prev => prev + 1);
      
      return newBlocks;
    });
    setHasChanges(true);
    setSaved(false);
  };

  // Update block
  const handleUpdateBlock = useCallback((blockId, updates) => {
    setBlocks(prevBlocks => {
      const newBlocks = prevBlocks.map(block => 
        block.id === blockId ? { ...block, ...updates } : block
      );
      
      setHistory(prev => {
        const newHistory = prev.slice(0, historyIndex + 1);
        newHistory.push(newBlocks);
        return newHistory;
      });
      setHistoryIndex(prev => prev + 1);
      
      return newBlocks;
    });
    setHasChanges(true);
    setSaved(false);
  }, [historyIndex]);

  // Select element for styling
  const handleSelectElement = (elementId) => {
    setSelectedElementId(elementId);
  };

  // Undo/Redo
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(prev => prev - 1);
      setBlocks(history[historyIndex - 1]);
      setHasChanges(true);
    }
  }, [history, historyIndex]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(prev => prev + 1);
      setBlocks(history[historyIndex + 1]);
      setHasChanges(true);
    }
  }, [history, historyIndex]);

  // Save
  const handleSave = useCallback(() => {
    setSaving(true);
    
    try {
      localStorage.setItem(`preview_saved_${landingId}`, JSON.stringify({
        blocks,
        timestamp: Date.now()
      }));

      if (window.opener) {
        window.opener.postMessage({
          type: 'PREVIEW_SAVE',
          landingId,
          blocks
        }, '*');
      }

      setHasChanges(false);
      setSaved(true);
      toast.success('Cambios guardados correctamente');
      
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error('Error saving:', e);
      toast.error('Error al guardar los cambios');
    } finally {
      setSaving(false);
    }
  }, [landingId, blocks]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) handleRedo();
        else handleUndo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave, handleUndo, handleRedo]);

  // Close panel when clicking outside
  const handleCanvasClick = (e) => {
    if (e.target === e.currentTarget) {
      setSelectedElementId(null);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      if (window.confirm('Tienes cambios sin guardar. ¿Deseas guardarlos antes de salir?')) {
        handleSave();
      }
    }
    window.close();
  };

  if (blocks.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-500 animate-spin mx-auto mb-4" />
          <p className="text-white/60">Cargando vista previa...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Toolbar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-gray-900/95 backdrop-blur-lg border-b border-white/10 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleClose}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-white font-bold">Editor Visual en Vivo</h1>
              <p className="text-white/60 text-sm">Haz clic en cualquier elemento para editarlo</p>
            </div>
          </div>

          <div className="flex items-center gap-2 bg-white/10 rounded-lg p-1">
            <button
              onClick={() => setViewMode('desktop')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'desktop' ? 'bg-indigo-500 text-white' : 'text-white/60 hover:text-white'
              }`}
            >
              <Monitor className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('mobile')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'mobile' ? 'bg-indigo-500 text-white' : 'text-white/60 hover:text-white'
              }`}
            >
              <Smartphone className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1">
              <button
                onClick={handleUndo}
                disabled={historyIndex <= 0}
                className="p-2 rounded-md text-white/60 hover:text-white disabled:opacity-30 transition-colors"
                title="Deshacer (Ctrl+Z)"
              >
                <Undo className="w-4 h-4" />
              </button>
              <button
                onClick={handleRedo}
                disabled={historyIndex >= history.length - 1}
                className="p-2 rounded-md text-white/60 hover:text-white disabled:opacity-30 transition-colors"
                title="Rehacer (Ctrl+Shift+Z)"
              >
                <Redo className="w-4 h-4" />
              </button>
            </div>

            {/* Style Panel Indicator */}
            {selectedElementId && (
              <span className="px-3 py-2 rounded-lg bg-indigo-500/20 text-indigo-400 text-sm flex items-center gap-2">
                <Paintbrush className="w-4 h-4" />
                Editando elemento
              </span>
            )}

            {hasChanges && (
              <span className="text-amber-400 text-sm bg-amber-500/20 px-3 py-1.5 rounded-lg">
                Sin guardar
              </span>
            )}

            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className={`px-6 transition-all duration-300 ${
                saved ? 'bg-green-500 hover:bg-green-600' : 'bg-indigo-500 hover:bg-indigo-600'
              } text-white`}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : saved ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Guardado
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Guardar
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Style Editor Panel */}
      <StyleEditorPanel
        isOpen={selectedElementId !== null}
        onClose={() => {
          setSelectedElementId(null);
        }}
        elementStyles={getCurrentElementStyles()}
        onStyleChange={handleStyleChange}
        brandColors={brandColors}
      />

      {/* Landing Preview */}
      <div 
        className={`pt-20 transition-all duration-300 ${viewMode === 'mobile' ? 'px-4 py-8' : ''} ${selectedElementId ? 'mr-80' : ''}`}
        onClick={handleCanvasClick}
      >
        {/* Meta Pixel Tracker - fires appropriate events */}
        {briefData?.meta_pixel_id && (
          <MetaPixelTracker
            pixelId={briefData.meta_pixel_id}
            landingSlug={landingId}
            briefData={briefData}
          />
        )}
        
        <div 
          className={`mx-auto transition-all duration-500 ${
            viewMode === 'mobile' 
              ? 'max-w-sm shadow-2xl rounded-3xl overflow-hidden border-8 border-gray-800' 
              : 'w-full'
          }`}
        >
          {blocks.map((block) => (
            <BlockRenderer
              key={block.id}
              block={block}
              brandColors={brandColors}
              expertPhoto={expertPhoto}
              onUpdate={handleUpdateBlock}
              onSelectElement={handleSelectElement}
              selectedElementId={selectedElementId}
            />
          ))}
        </div>
      </div>

      {/* Help Panel */}
      {!selectedElementId && (
        <div className="fixed bottom-6 right-6 bg-gray-900/95 backdrop-blur-lg rounded-2xl p-4 border border-white/10 shadow-2xl max-w-sm">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
              <Type className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold mb-1">Editor WYSIWYG Activo</h3>
              <p className="text-white/60 text-sm">
                Haz clic en cualquier texto para seleccionarlo y abrir el panel de estilos.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPreview;
